import React, { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { useTranslation } from 'react-i18next';
import Loader from '../Utility/Loader/Loader';
import CustomDatePicker from '../Utility/DatePicker/DatePicker';

/**
 * Splits `text` on case-insensitive occurrences of `phrase` and replaces each match with
 * an uppercase, bold rendering of the phrase. Used so PIM destructive-confirm modals
 * always show the required word as e.g. `CONFIRM` regardless of the source string's casing.
 */
function renderHelperWithBoldPhrase(text, phrase) {
  if (!text || !phrase) return text;
  const escaped = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const upper = phrase.toUpperCase();
  const parts = String(text).split(new RegExp(`(${escaped})`, 'gi'));
  return parts.map((part, i) => (part.toLowerCase() === phrase.toLowerCase() ? <strong key={`p${i}`}>{upper}</strong> : part));
}

/** Shared confirmation modal. PIM destructive actions opt into typed-phrase gating via `requireTypedConfirmation`. */
function ConfirmationPopup({
  onOpen,
  onConfirm,
  message,
  secondMessage,
  toggle,
  title,
  loading,
  commentText,
  isCommentOn,
  showCheckBox,
  disabledBtn,
  showThirdMsg,
  thirdMessage,
  showDate,
  setSelectedDate,
  selectedDate,
  comment,
  showCommentHeader,
  isReferenceCode,
  code,
  handleRefrenceCode,
  isDuplicateLibrary,
  isDuplicatetemplate,
  handleDuplicateName,
  showDateError,
  isGrading,
  moduleName,
  isSpec,
  fromAccountDetails,
  fromSupplier,
  isFileBin,
  requireTypedConfirmation,
  typedConfirmationPhrase,
  typedConfirmationHelperText,
  confirmButtonLabel,
  hideFooterActions,
}) {
  const { t } = useTranslation('common');
  const [allowDelete, setAllowDelete] = useState(false);
  const [typedPhrase, setTypedPhrase] = useState('');
  const expectedPhrase = (typedConfirmationPhrase || 'confirm').toUpperCase();
  /*
   * Case-sensitive, as the typed surface promises. The helper label renders the phrase uppercase,
   * so a strict compare quietly required CONFIRM and left the button dead for anyone who typed the
   * word as it reads in the sentence.
   */
  const phraseMatched = typedPhrase.trim() === expectedPhrase;
  const helperText = typedConfirmationHelperText || `Type ${expectedPhrase} to continue`;
  const helperLabel = renderHelperWithBoldPhrase(helperText, expectedPhrase);
  const confirmLabel = confirmButtonLabel || 'Confirm';
  const handleClose = () => {
    setTypedPhrase('');
    if (toggle) toggle();
  };
  const handleConfirm = () => {
    if (requireTypedConfirmation && !phraseMatched) return;
    setTypedPhrase('');
    if (onConfirm) onConfirm();
  };
  const deleteCompletly = (e) => {
    if (showCheckBox) {
      setAllowDelete(e.target.checked);
    }
  };

  const handleDateChange = (e) => {
    setSelectedDate(e);
  };
  const renderFooterActions = () => {
    if (hideFooterActions) {
      return null;
    }
    if (fromAccountDetails) {
      return (
        <div className="common-model-footer">
          <button onClick={handleClose} className="btn btn-default" type="button">
            {t('CONFIRMATION_POPUP.OK')}
          </button>
        </div>
      );
    }
    if (requireTypedConfirmation) {
      return (
        <div className="common-model-footer">
          <button type="button" className="btn btn-primary" onClick={handleConfirm} disabled={!phraseMatched || loading || disabledBtn}>
            {confirmLabel}
          </button>
        </div>
      );
    }
    return (
      <div className="common-model-footer">
        <button onClick={onConfirm} type="button" className="btn p-0" disabled={(showCheckBox && !allowDelete) || disabledBtn}>
          <span className="icon-Tick-1 f-24" />
        </button>
      </div>
    );
  };

  return (
    <Modal show={onOpen} backdrop="static" className="common-custom-modal modal-medium" aria-labelledby="contained-modal-title-vcenter" onHide={handleClose}>
      <Modal.Header>
        <Modal.Title id="contained-modal-title-vcenter">
          <span>{title}</span>
        </Modal.Title>
        <span onClick={handleClose} className="icon-close cursor-pointer" />
      </Modal.Header>
      <Modal.Body>
        {loading && <Loader />}
        {!loading && (
          <>
            <div className="modal-inner-data">
              <span>
                {message && <p className="mb-16 text-center">{t`${message}`}</p>}
                {showCheckBox ? (
                  <div className="checkbox-label">
                    <input type="checkbox" id="delConfirm" name="delConfirm" onChange={deleteCompletly} value="delete" />
                    <label htmlFor="delConfirm" className="chklabel align-items-start">
                      <p className="ml-2">{secondMessage}</p>
                    </label>
                  </div>
                ) : (
                  secondMessage && <p className="mb-16 text-center">{secondMessage}</p>
                )}
              </span>
              {showThirdMsg && (
                <span>
                  <p className="text-center">{thirdMessage}</p>
                </span>
              )}
              {showDate && (
                <div className="date justify-content-end px-1">
                  <div className="input-button-container">
                    <CustomDatePicker
                      dateFormat="dd-MMM-yyyy"
                      name="deliveryDate"
                      popperPlace="bottom-end"
                      placeholder={t('CONFIRMATION_POPUP.DELIVERY_DATE_LABEL')}
                      onChange={handleDateChange}
                      valueProp={selectedDate ? new Date(selectedDate) : null}
                    />
                  </div>
                  {!selectedDate && showDateError && <small className="error-message">{t('CONFIRMATION_POPUP.DELIVERY_DATE_REQUIRED')}</small>}
                </div>
              )}
              {isCommentOn && (
                <div className="common-input-control">
                  {showCommentHeader && (
                    <label>
                      <strong>{t('CONFIRMATION_POPUP.COMMENT_LABEL')}</strong>
                    </label>
                  )}
                  <input
                    className="form-control"
                    rows="3"
                    onChange={commentText}
                    value={comment}
                    placeholder={t('CONFIRMATION_POPUP.ENTER_COMMENT')}
                    maxLength="200"
                  />
                  <div className="character-counter">{comment?.length || 0}/200</div>
                </div>
              )}
              {isReferenceCode && (
                <div className="common-input-control reference-input mb-0 fixed-label">
                  <label className="mb-16">
                    <span className="font-heavy">{t('CONFIRMATION_POPUP.REFERENCE_CODE_LABEL')}</span>
                  </label>
                  <input className="form-control text-bordered" onChange={handleRefrenceCode} value={code} />
                </div>
              )}
              {isDuplicateLibrary && (
                <div className="input-group form-floating-label fixed-label">
                  <input
                    type="text"
                    placeholder={isSpec ? t('CONFIRMATION_POPUP.CODE') : t('CONFIRMATION_POPUP.NAME')}
                    onChange={handleDuplicateName}
                    className="form-control"
                  />
                  <label className="f-14 asterisk-req">
                    {isGrading || isSpec ? moduleName : t('CONFIRMATION_POPUP.CUSTOM_MODULE_NAME_LABEL')} <span>*</span>
                  </label>
                </div>
              )}
              {isDuplicatetemplate && (
                <div className="input-group form-floating-label fixed-label">
                  <input
                    type="text"
                    placeholder={isSpec ? t('CONFIRMATION_POPUP.CODE') : t('CONFIRMATION_POPUP.NAME')}
                    onChange={handleDuplicateName}
                    className="form-control"
                  />
                  <label className="f-14 asterisk-req">
                    {isGrading || isSpec ? moduleName : t('CONFIRMATION_POPUP.TEMPLATE_NAME')} <span>*</span>
                  </label>
                </div>
              )}
              {isFileBin && <span>{t('CONFIRMATION_POPUP.REMOVE_FOLDER_MESSAGE')}</span>}
              {requireTypedConfirmation && (
                <div className="common-input-control mt-3">
                  <label className="f-14">{helperLabel}</label>
                  <input
                    type="text"
                    className="form-control"
                    value={typedPhrase}
                    onChange={(e) => setTypedPhrase(e.target.value)}
                    placeholder={expectedPhrase}
                    autoFocus
                    aria-label={helperText}
                  />
                </div>
              )}
            </div>
            {renderFooterActions()}
          </>
        )}
      </Modal.Body>
    </Modal>
  );
}

export default ConfirmationPopup;
